import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  IconButton,
  Divider,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  OutlinedInput,
  Checkbox,
  ListItemText
} from '@mui/material';
import {
  Print as PrintIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  MenuBook as MenuBookIcon,
  Tune as TuneIcon,
  SortByAlpha as SortByAlphaIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { customerService } from '../../services/customerService';
import { formatBarangayName } from '../../services/reportService';
import { authService } from '../../authService';

const CustomerLogbookPage = () => {
  const [customers, setCustomers] = useState([]);
  const [barangays, setBarangays] = useState([]);
  const [availableTypes, setAvailableTypes] = useState(['Residential', 'Commercial', 'Government', 'Institutional', 'Industrial']);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // 1. Core Filters (Default: Active, Residential & Commercial, Alphabetical A-Z)
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBarangay, setSelectedBarangay] = useState('all');
  const [selectedTypes, setSelectedTypes] = useState(['Residential', 'Commercial']); // Residential & Commercial default
  const [selectedStatus, setSelectedStatus] = useState('Active'); // Active default
  const [sortOrder, setSortOrder] = useState('asc'); // Alphabetical A-Z default

  // 2. Minimal Rectangular Name Tag / Card Print Options
  const [namesPerRow, setNamesPerRow] = useState(3); // Default to 3 per row for minimal, compact layout
  const [pageSize, setPageSize] = useState('letter'); // letter, legal, a4
  const [pageOrientation, setPageOrientation] = useState('portrait'); // portrait, landscape
  const [cardDensity, setCardDensity] = useState('minimal'); // minimal (42px), super_compact (34px), standard (54px)
  const [cardBorderStyle, setCardBorderStyle] = useState('solid'); // solid, dashed, minimal
  const [nameFontSize, setNameFontSize] = useState('small'); // small (0.82rem), medium (0.95rem), large (1.1rem)
  const [textAlign, setTextAlign] = useState('center'); // center, left
  const [showPrintHeader, setShowPrintHeader] = useState(false); // Default hidden to maximize printable tags

  // Customization Dialog
  const [showCustomModal, setShowCustomModal] = useState(false);

  const printAreaRef = useRef(null);
  const user = authService.getUserSession();

  // Load Initial Data
  useEffect(() => {
    loadCustomerData();
  }, []);

  const loadCustomerData = async () => {
    setLoading(true);
    setError('');
    try {
      const [custRes, brgyRes, typeRes] = await Promise.all([
        customerService.getCustomers(),
        customerService.getBarangays(),
        customerService.getCustomerTypes()
      ]);

      if (custRes.success) {
        setCustomers(custRes.data || []);
      } else {
        setError(custRes.error || 'Failed to fetch customers');
      }

      if (brgyRes.success && brgyRes.data) {
        setBarangays(brgyRes.data);
      }

      if (typeRes.success && typeRes.data && typeRes.data.length > 0) {
        const types = typeRes.data.map((t) => t.type).filter(Boolean);
        if (types.length > 0) {
          setAvailableTypes(Array.from(new Set([...types, 'Residential', 'Commercial'])));
        }
      }
    } catch (err) {
      console.error('Error loading customer logbook records:', err);
      setError('An error occurred while loading customer records.');
    } finally {
      setLoading(false);
    }
  };

  // Filter & Alphabetical Sort
  const filteredCustomers = useMemo(() => {
    return customers
      .filter((c) => {
        // 1. Status Filter (Default: Active)
        if (selectedStatus !== 'all') {
          const cStatus = (c.status || 'Active').toLowerCase();
          if (cStatus !== selectedStatus.toLowerCase()) {
            return false;
          }
        }

        // 2. Customer Type Filter (Default: Residential & Commercial)
        if (selectedTypes.length > 0) {
          const cType = (c.type || '').toLowerCase();
          const matched = selectedTypes.some((t) => t.toLowerCase() === cType);
          if (!matched) {
            return false;
          }
        }

        // 3. Barangay Filter
        if (selectedBarangay !== 'all') {
          const cBrgy = (c.barangay || '').toLowerCase();
          if (cBrgy !== selectedBarangay.toLowerCase()) {
            return false;
          }
        }

        // 4. Search Query (Name, Customer ID)
        if (searchTerm.trim()) {
          const term = searchTerm.trim().toLowerCase();
          const nameMatch = c.name && c.name.toLowerCase().includes(term);
          const idMatch = c.customerid && String(c.customerid).toLowerCase().includes(term);
          if (!nameMatch && !idMatch) {
            return false;
          }
        }

        return true;
      })
      .sort((a, b) => {
        // Alphabetical sorting by Customer Name (A-Z or Z-A)
        const nameA = (a.name || '').trim().toLowerCase();
        const nameB = (b.name || '').trim().toLowerCase();
        if (sortOrder === 'desc') {
          return nameB.localeCompare(nameA);
        }
        return nameA.localeCompare(nameB);
      });
  }, [customers, selectedStatus, selectedTypes, selectedBarangay, searchTerm, sortOrder]);

  // Handle Customer Types Multi-select change
  const handleTypeChange = (event) => {
    const {
      target: { value }
    } = event;
    setSelectedTypes(typeof value === 'string' ? value.split(',') : value);
  };

  // Print Action
  const handlePrint = () => {
    window.print();
  };

  // Excel Export
  const handleExportExcel = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Customer Name Tags');

      worksheet.addRow(['LGU CONCEPCION - WATERWORKS SYSTEM']);
      worksheet.addRow(['CUSTOMER NAME TAGS']);
      worksheet.addRow([
        `Barangay: ${selectedBarangay === 'all' ? 'All Barangays' : selectedBarangay} | Types: ${selectedTypes.join(', ')} | Status: ${selectedStatus}`
      ]);
      worksheet.addRow([]);

      worksheet.addRow(['#', 'Customer Name', 'Account Number', 'Type', 'Status']);

      filteredCustomers.forEach((c, idx) => {
        worksheet.addRow([
          idx + 1,
          c.name || '',
          c.customerid || '',
          c.type || '',
          c.status || 'Active'
        ]);
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(blob, `Customer_Name_Tags_${selectedBarangay}_${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (err) {
      console.error('Error exporting to Excel:', err);
    }
  };

  // Sizing Styles Mapping (Minimal, Non-Spacious)
  const cardHeightPx = useMemo(() => {
    switch (cardDensity) {
      case 'super_compact':
        return '36px';
      case 'standard':
        return '54px';
      case 'minimal':
      default:
        return '44px';
    }
  }, [cardDensity]);

  const cardPadding = useMemo(() => {
    switch (cardDensity) {
      case 'super_compact':
        return '2px 4px';
      case 'standard':
        return '6px 8px';
      case 'minimal':
      default:
        return '3px 6px';
    }
  }, [cardDensity]);

  const nameFontStyles = useMemo(() => {
    switch (nameFontSize) {
      case 'small':
        return { fontSize: '0.88rem', fontWeight: 900, letterSpacing: '0.01em' };
      case 'large':
        return { fontSize: '1.18rem', fontWeight: 900, letterSpacing: '0.02em' };
      case 'medium':
      default:
        return { fontSize: '1.02rem', fontWeight: 900, letterSpacing: '0.015em' };
    }
  }, [nameFontSize]);

  // Border CSS mapping
  const borderCss = useMemo(() => {
    switch (cardBorderStyle) {
      case 'dashed':
        return '1px dashed #334155';
      case 'minimal':
        return '1px solid #94a3b8';
      case 'solid':
      default:
        return '1px solid #1e293b';
    }
  }, [cardBorderStyle]);

  // Estimated sheets calculation
  const cardsPerPageEst = useMemo(() => {
    const rowsEst = cardDensity === 'super_compact' ? 22 : cardDensity === 'minimal' ? 17 : 13;
    return namesPerRow * rowsEst;
  }, [namesPerRow, cardDensity]);

  const estimatedSheets = Math.max(1, Math.ceil(filteredCustomers.length / cardsPerPageEst));

  return (
    <Box sx={{ pb: 6 }}>
      {/* Minimal Dynamic Print CSS */}
      <style>{`
        @media print {
          @page {
            size: ${pageSize} ${pageOrientation};
            margin: 4mm 4mm;
          }
          body {
            background-color: #ffffff !important;
            color: #000000 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          /* Hide UI Chrome */
          header, nav, aside, .MuiAppBar-root, .MuiDrawer-root, .no-print, .MuiToolbar-root, button, .MuiPagination-root {
            display: none !important;
          }
          main, .MuiBox-root {
            padding: 0 !important;
            margin: 0 !important;
            width: 100% !important;
            max-width: 100% !important;
            background: transparent !important;
          }
          .name-tags-print-container {
            display: block !important;
            width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
            border: none !important;
            box-shadow: none !important;
          }
          .name-tags-grid {
            display: grid !important;
            grid-template-columns: repeat(${namesPerRow}, 1fr) !important;
            gap: 2.5px !important;
            width: 100% !important;
          }
          .name-tag-card {
            border: ${borderCss} !important;
            break-inside: avoid !important;
            page-break-inside: avoid !important;
            background-color: #ffffff !important;
            color: #000000 !important;
            box-shadow: none !important;
            padding: ${cardPadding} !important;
            min-height: ${cardHeightPx} !important;
          }
          .print-header-bar {
            display: ${showPrintHeader ? 'block' : 'none'} !important;
            margin-bottom: 4px !important;
            border-bottom: 1px solid #000000 !important;
            padding-bottom: 2px !important;
          }
        }
      `}</style>

      {/* Screen Title & Quick Actions */}
      <Box className="no-print" sx={{ mb: 2.5 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2}>
          <Box>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  p: 0.9,
                  borderRadius: 2,
                  bgcolor: '#ecfdf5',
                  color: '#059669',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 6px rgba(5, 150, 105, 0.15)'
                }}
              >
                <MenuBookIcon sx={{ fontSize: 24 }} />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={800} color="#0f172a">
                  Customer Name Tag Printing
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Compact name tags with Customer Name, Account Number, and Type.
                </Typography>
              </Box>
            </Stack>
          </Box>

          {/* Action Buttons */}
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button
              variant="outlined"
              color="inherit"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={loadCustomerData}
              disabled={loading}
              sx={{ bgcolor: '#ffffff', borderColor: '#cbd5e1' }}
            >
              Refresh
            </Button>
            <Button
              variant="outlined"
              color="primary"
              size="small"
              startIcon={<TuneIcon />}
              onClick={() => setShowCustomModal(true)}
              sx={{ bgcolor: '#ffffff' }}
            >
              Density & Layout
            </Button>
            <Button
              variant="outlined"
              color="success"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={handleExportExcel}
              disabled={loading || filteredCustomers.length === 0}
              sx={{ bgcolor: '#ffffff' }}
            >
              Excel
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
              disabled={loading || filteredCustomers.length === 0}
              sx={{
                bgcolor: '#059669',
                '&:hover': { bgcolor: '#047857' },
                fontWeight: 700,
                boxShadow: '0 4px 10px rgba(5, 150, 105, 0.2)'
              }}
            >
              Print ({filteredCustomers.length})
            </Button>
          </Stack>
        </Stack>
      </Box>

      {/* Main Filter & Grid Controls Card */}
      <Card elevation={0} className="no-print" sx={{ mb: 2.5, border: '1px solid #e2e8f0', borderRadius: 2, bgcolor: '#ffffff' }}>
        <CardContent sx={{ p: 2 }}>
          <Grid container spacing={1.5} alignItems="center">
            {/* Search Box */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search name or account #..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" sx={{ color: '#94a3b8' }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchTerm('')}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>

            {/* Barangay Filter */}
            <Grid item xs={6} sm={3} md={2.5}>
              <FormControl fullWidth size="small">
                <InputLabel>Barangay</InputLabel>
                <Select
                  value={selectedBarangay}
                  label="Barangay"
                  onChange={(e) => setSelectedBarangay(e.target.value)}
                >
                  <MenuItem value="all">All Barangays</MenuItem>
                  {barangays.map((b) => (
                    <MenuItem key={b.barangay || b.name} value={b.barangay || b.name}>
                      {formatBarangayName(b.barangay || b.name)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Customer Type Multi-Select (Default: Residential & Commercial) */}
            <Grid item xs={6} sm={3} md={2.5}>
              <FormControl fullWidth size="small">
                <InputLabel>Customer Types</InputLabel>
                <Select
                  multiple
                  value={selectedTypes}
                  onChange={handleTypeChange}
                  input={<OutlinedInput label="Customer Types" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((val) => (
                        <Chip key={val} label={val} size="small" sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700 }} />
                      ))}
                    </Box>
                  )}
                >
                  {availableTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      <Checkbox checked={selectedTypes.indexOf(type) > -1} size="small" />
                      <ListItemText primary={type} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Status Select (Default: Active) */}
            <Grid item xs={6} sm={3} md={1.5}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={selectedStatus}
                  label="Status"
                  onChange={(e) => setSelectedStatus(e.target.value)}
                >
                  <MenuItem value="Active">Active Only</MenuItem>
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="Disconnected">Disconnected</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Names Per Row Selector (1 to 6 per row) */}
            <Grid item xs={6} sm={3} md={2.5}>
              <FormControl fullWidth size="small">
                <InputLabel>Names Per Row</InputLabel>
                <Select
                  value={namesPerRow}
                  label="Names Per Row"
                  onChange={(e) => setNamesPerRow(Number(e.target.value))}
                >
                  <MenuItem value={1}>1 Per Row</MenuItem>
                  <MenuItem value={2}>2 Per Row</MenuItem>
                  <MenuItem value={3}>3 Per Row (Recommended)</MenuItem>
                  <MenuItem value={4}>4 Per Row (Compact)</MenuItem>
                  <MenuItem value={5}>5 Per Row (Ultra-Compact)</MenuItem>
                  <MenuItem value={6}>6 Per Row (Mini)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Quick Layout Ribbon */}
          <Divider sx={{ my: 1.5 }} />
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems="center" spacing={1} flexWrap="wrap">
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography variant="caption" fontWeight={700} color="text.secondary">
                COLUMNS:
              </Typography>

              {[2, 3, 4, 5, 6].map((cols) => (
                <Chip
                  key={cols}
                  label={`${cols} / row`}
                  size="small"
                  clickable
                  variant={namesPerRow === cols ? 'filled' : 'outlined'}
                  color={namesPerRow === cols ? 'primary' : 'default'}
                  onClick={() => setNamesPerRow(cols)}
                  sx={{
                    height: 22,
                    fontSize: '0.72rem',
                    fontWeight: namesPerRow === cols ? 700 : 500,
                    bgcolor: namesPerRow === cols ? '#059669' : 'transparent',
                    borderColor: '#cbd5e1'
                  }}
                />
              ))}

              <Divider orientation="vertical" flexItem sx={{ mx: 0.5, height: 16 }} />

              {/* Density Toggle */}
              <Typography variant="caption" fontWeight={700} color="text.secondary">
                HEIGHT:
              </Typography>
              {[
                { id: 'super_compact', label: 'Tight' },
                { id: 'minimal', label: 'Minimal' },
                { id: 'standard', label: 'Standard' }
              ].map((d) => (
                <Chip
                  key={d.id}
                  label={d.label}
                  size="small"
                  clickable
                  variant={cardDensity === d.id ? 'filled' : 'outlined'}
                  color={cardDensity === d.id ? 'primary' : 'default'}
                  onClick={() => setCardDensity(d.id)}
                  sx={{
                    height: 22,
                    fontSize: '0.72rem',
                    fontWeight: cardDensity === d.id ? 700 : 500,
                    bgcolor: cardDensity === d.id ? '#0f172a' : 'transparent',
                    borderColor: '#cbd5e1'
                  }}
                />
              ))}

              <Divider orientation="vertical" flexItem sx={{ mx: 0.5, height: 16 }} />

              {/* Alphabetical Sort Toggle */}
              <Button
                size="small"
                variant="outlined"
                startIcon={<SortByAlphaIcon sx={{ fontSize: 14 }} />}
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                sx={{
                  height: 22,
                  fontSize: '0.7rem',
                  borderColor: '#cbd5e1',
                  color: '#0f172a',
                  fontWeight: 700
                }}
              >
                {sortOrder === 'asc' ? 'A → Z' : 'Z → A'}
              </Button>
            </Stack>

            {/* Quick Summary Pill */}
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                label={`${filteredCustomers.length} Active`}
                size="small"
                color="success"
                variant="outlined"
                sx={{ height: 22, fontSize: '0.72rem', fontWeight: 700 }}
              />
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                ~<strong>{estimatedSheets}</strong> {estimatedSheets === 1 ? 'Sheet' : 'Sheets'} ({pageSize.toUpperCase()})
              </Typography>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* Main Printable Rectangular Name Tags Container (Minimal & Non-Spacious) */}
      <Card
        elevation={0}
        ref={printAreaRef}
        className="name-tags-print-container"
        sx={{
          border: '1px solid #cbd5e1',
          borderRadius: 2,
          bgcolor: '#ffffff',
          boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
          p: { xs: 1.5, sm: 2, md: 2.5 }
        }}
      >
        {/* Printable Header Bar (Optional) */}
        {showPrintHeader && (
          <Box className="print-header-bar" sx={{ mb: 1.5, pb: 0.5, borderBottom: '1px solid #0f172a' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="caption" fontWeight={800} color="#0f172a">
                WATERWORKS NAME TAGS &bull; {selectedBarangay === 'all' ? 'ALL BARANGAYS' : formatBarangayName(selectedBarangay).toUpperCase()}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {filteredCustomers.length} Customers (A-Z) &bull; {new Date().toLocaleDateString()}
              </Typography>
            </Stack>
          </Box>
        )}

        {/* Loading State */}
        {loading ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <CircularProgress size={32} sx={{ color: '#059669', mb: 1.5 }} />
            <Typography variant="caption" color="text.secondary" display="block">
              Loading customer name tags...
            </Typography>
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ my: 2 }}>
            {error}
          </Alert>
        ) : filteredCustomers.length === 0 ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary" fontWeight={700}>
              No customer names match the current filters.
            </Typography>
          </Box>
        ) : (
          /* MINIMAL RECTANGULAR NAME TAGS GRID: Customer Name, Account Number, and Type ONLY */
          <Box
            className="name-tags-grid"
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: namesPerRow === 1 ? '1fr' : `repeat(${Math.min(namesPerRow, 2)}, 1fr)`,
                md: `repeat(${namesPerRow}, 1fr)`
              },
              gap: '4px'
            }}
          >
            {filteredCustomers.map((cust, index) => {
              return (
                <Box
                  key={cust.customerid || index}
                  className="name-tag-card"
                  sx={{
                    minHeight: cardHeightPx,
                    border: borderCss,
                    borderRadius: cardBorderStyle === 'dashed' ? '0px' : '3px',
                    p: cardPadding,
                    bgcolor: '#ffffff',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: textAlign === 'center' ? 'center' : 'flex-start',
                    textAlign: textAlign,
                    position: 'relative',
                    boxSizing: 'border-box',
                    '&:hover': {
                      borderColor: '#059669'
                    }
                  }}
                >
                  {/* 1. Customer Main Name (Prominent, Bold, Highlighted) */}
                  <Typography
                    variant="body2"
                    sx={{
                      ...nameFontStyles,
                      color: '#000000',
                      textTransform: 'uppercase',
                      lineHeight: 1.15,
                      wordBreak: 'break-word',
                      mb: 0.2
                    }}
                  >
                    {cust.name || 'UNNAMED CUSTOMER'}
                  </Typography>

                  {/* 2. Account Number & 3. Type (Minimized subtle secondary line) */}
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: '0.62rem',
                      fontWeight: 600,
                      color: '#64748b',
                      lineHeight: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.4,
                      letterSpacing: '0.01em'
                    }}
                  >
                    <span>#{cust.customerid}</span>
                    <span style={{ opacity: 0.4 }}>&bull;</span>
                    <span style={{ color: '#64748b', textTransform: 'capitalize' }}>
                      {cust.type || 'Residential'}
                    </span>
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}
      </Card>

      {/* Layout & Print Customization Dialog */}
      <Dialog
        open={showCustomModal}
        onClose={() => setShowCustomModal(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800, display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <TuneIcon sx={{ color: '#059669', fontSize: 20 }} />
            <Typography variant="subtitle1" fontWeight={800}>Layout & Density Settings</Typography>
          </Stack>
          <IconButton size="small" onClick={() => setShowCustomModal(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 2.5 }}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Names Per Row</InputLabel>
                <Select
                  value={namesPerRow}
                  label="Names Per Row"
                  onChange={(e) => setNamesPerRow(Number(e.target.value))}
                >
                  <MenuItem value={1}>1 Per Row</MenuItem>
                  <MenuItem value={2}>2 Per Row</MenuItem>
                  <MenuItem value={3}>3 Per Row</MenuItem>
                  <MenuItem value={4}>4 Per Row</MenuItem>
                  <MenuItem value={5}>5 Per Row</MenuItem>
                  <MenuItem value={6}>6 Per Row</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Card Height</InputLabel>
                <Select
                  value={cardDensity}
                  label="Card Height"
                  onChange={(e) => setCardDensity(e.target.value)}
                >
                  <MenuItem value="super_compact">Tight (36px)</MenuItem>
                  <MenuItem value="minimal">Minimal (44px)</MenuItem>
                  <MenuItem value="standard">Standard (54px)</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Paper Size</InputLabel>
                <Select
                  value={pageSize}
                  label="Paper Size"
                  onChange={(e) => setPageSize(e.target.value)}
                >
                  <MenuItem value="letter">Letter (8.5 x 11 in)</MenuItem>
                  <MenuItem value="legal">Legal (8.5 x 14 in)</MenuItem>
                  <MenuItem value="a4">A4 (210 x 297 mm)</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Orientation</InputLabel>
                <Select
                  value={pageOrientation}
                  label="Orientation"
                  onChange={(e) => setPageOrientation(e.target.value)}
                >
                  <MenuItem value="portrait">Portrait</MenuItem>
                  <MenuItem value="landscape">Landscape</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Border Style</InputLabel>
                <Select
                  value={cardBorderStyle}
                  label="Border Style"
                  onChange={(e) => setCardBorderStyle(e.target.value)}
                >
                  <MenuItem value="solid">Thin Solid Line</MenuItem>
                  <MenuItem value="dashed">Dashed Cut Lines</MenuItem>
                  <MenuItem value="minimal">Faint Gray Line</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Name Font Size</InputLabel>
                <Select
                  value={nameFontSize}
                  label="Name Font Size"
                  onChange={(e) => setNameFontSize(e.target.value)}
                >
                  <MenuItem value="small">Small (0.82rem)</MenuItem>
                  <MenuItem value="medium">Medium (0.92rem)</MenuItem>
                  <MenuItem value="large">Large (1.05rem)</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Text Alignment</InputLabel>
                <Select
                  value={textAlign}
                  label="Text Alignment"
                  onChange={(e) => setTextAlign(e.target.value)}
                >
                  <MenuItem value="center">Centered</MenuItem>
                  <MenuItem value="left">Left Aligned</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 2.5, py: 1.5 }}>
          <Button onClick={() => setShowCustomModal(false)} variant="contained" size="small" sx={{ bgcolor: '#059669', '&:hover': { bgcolor: '#047857' } }}>
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomerLogbookPage;
